import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export interface AuditLogEntry {
  id: string;
  org_id: string;
  actor_user_id: string | null;
  /** Set when the action was taken via Manage as / takeover: the user the
   *  actor was working on behalf of. */
  on_behalf_of_user_id?: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface RetentionSetting {
  message_retention_days: number | null;
}

export function useAuditLog(filters: { action_prefix?: string; limit?: number } = {}) {
  return useQuery<{ data: AuditLogEntry[] }>({
    queryKey: ['compliance', 'audit-log', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: AuditLogEntry[] }>('/compliance/audit-log', {
        params: filters,
      });
      return data;
    },
  });
}

export function useRetention() {
  return useQuery<{ data: RetentionSetting }>({
    queryKey: ['compliance', 'retention'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: RetentionSetting }>('/compliance/retention');
      return data;
    },
  });
}

export function useUpdateRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RetentionSetting) => {
      const { data } = await apiClient.put<{ data: RetentionSetting }>('/compliance/retention', payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance', 'retention'] });
      qc.invalidateQueries({ queryKey: ['compliance', 'audit-log'] });
    },
  });
}
